import {
  Box,
  Divider,
  HStack,
  ReactTable,
  Text,
  VStack,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { InsurancePolicy } from '../../../../../../../types/AdvisoryReport';
import useInsurancePolicyRisksTable from './useInsurancePolicyRisksTable';
import useAdvisoryReportCurrency from '../../../../../hooks/useAdvisoryReportCurrency';
import { toEuro } from '../../../../../../../../../sales-conversation/src/utils/stringUtils';

interface Props {
  insurancePolicy: InsurancePolicy;
}

const InsurancePolicyRisksTable: React.FC<Props> = ({ insurancePolicy }) => {
  const { t } = useTranslation();
  const { data, columns } = useInsurancePolicyRisksTable(insurancePolicy);
  const { currency } = useAdvisoryReportCurrency();
  const isTailoredPremium = !!insurancePolicy.is_tailored_premium;
  return (
    <>
      {/* Desktop/Tablet: Keep table */}
      <Box display={{ base: 'none', tablet: 'block' }}>
        <ReactTable
          variant="borderless"
          data={data}
          columns={columns}
          emptyText={t('ADVISORY-REPORTS_CURRENT-INSURANCE_EMPTY-COVERAGES')}
          borderBottom="none"
        />
      </Box>

      {/* Mobile: Card list per row */}
      <VStack
        display={{ base: 'flex', tablet: 'none' }}
        align="stretch"
        spacing="4"
      >
        {data.length === 0 && (
          <Text color="gray.500">
            {t('ADVISORY-REPORTS_CURRENT-INSURANCE_EMPTY-COVERAGES')}
          </Text>
        )}
        {data.map((row, idx) => {
          const coverageName = row.name;
          const premiumText = isTailoredPremium
            ? '-'
            : row.premium != null
            ? toEuro(row.premium, currency)
            : '-';
          const risk = row.risk_object || t('OTHER');
          const insuredValueText = isTailoredPremium
            ? '-'
            : row.insured_value != null
            ? toEuro(row.insured_value, currency)
            : '-';
          const deductibleText = row.deductible != null ? row.deductible : '-';
          return (
            <Box
              key={`${coverageName}-${idx}`}
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              p="4"
              bg="white"
              boxShadow="md"
            >
              <HStack justify="space-between" align="center" mb="2" gap="3">
                <Text fontWeight="600" color="gray.800" flex="1" noOfLines={2}>
                  {coverageName}
                </Text>
                <Text fontWeight="700" color="gray.800" flexShrink={0}>
                  {premiumText}
                </Text>
              </HStack>
              <Divider borderColor="gray.100" my="2" />
              <VStack align="stretch" spacing="3">
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="500">
                    {t('RISK')}
                  </Text>
                  <Text fontSize="sm" color="gray.800" fontWeight="600">
                    {risk}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="500">
                    {t(
                      'ADVISORY-CONVERSATION_NEW-REPORT_OFFERS_FORM_INSURED-CONTENT_LABEL',
                    )}
                  </Text>
                  <Text fontSize="sm" color="gray.800" fontWeight="600">
                    {insuredValueText}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="500">
                    {t('ADVISORY-REPORTS_DETAIL_OFFERS_OWN-RISK')}
                  </Text>
                  <Text fontSize="sm" color="gray.800" fontWeight="600">
                    {deductibleText}
                  </Text>
                </Box>
              </VStack>
            </Box>
          );
        })}
      </VStack>
    </>
  );
};

export default InsurancePolicyRisksTable;
